import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export type CampaignType = 'CANDIDATE' | 'BD' | 'JOB' | 'DEAL' | 'EVENT' | 'NEWSLETTER' | 'SMS_BLAST';
export type CampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'ARCHIVED';

/** Auto-enrollment trigger types — matches the backend's supported
 *  TriggerEvent shapes. `null` config means the campaign only enrolls its
 *  static audience snapshot at launch.
 *
 *  - `application_stage_change`: a candidate's job-application moved stage
 *    (real JT ApplicationStage). Optionally scoped to one job. Enrols the candidate.
 *  - `deal_stage_change`: a deal card moved stage on a pipeline. Enrols the
 *    contact(s) linked to the card. pipeline_id is required.
 *  - `list_added` / `new_placement`: unchanged.
 *  - `stage_change` (legacy): fires on a candidate's `type` field — not surfaced
 *    in the builder UI anymore, kept for back-compat.
 */
export type TriggerKind =
  | 'application_stage_change'
  | 'deal_stage_change'
  | 'application_stage_dwell'
  | 'deal_stage_dwell'
  | 'meeting_booked'
  | 'list_added'
  | 'new_placement'
  | 'stage_change';

export interface TriggerConfig {
  type: TriggerKind;
  /** Match criteria evaluated against each TriggerEvent's payload.
   *  Unknown keys are ignored by the backend's matcher so this is forward-compat. */
  match?: {
    /** application_stage_change: scope to one job + target/previous stage. */
    job_id?: string;
    to_stage?: string;
    from_stage?: string;
    /** deal_stage_change: required pipeline + optional target/previous stage id. */
    pipeline_id?: string;
    to_stage_id?: string;
    from_stage_id?: string;
    /** *_stage_dwell: the stage the card/application sits in + how many days it
     *  must have been there. Deal dwell uses stage_id (+ pipeline_id); job dwell
     *  uses stage (ApplicationStage) (+ optional job_id). */
    stage_id?: string;
    stage?: string;
    dwell_days?: number;
    /** list_added / new_placement. */
    list_id?: string;
    status?: string;
    employment_type?: string;
    /** meeting_booked: optionally scope to one booking-link owner's calendar. */
    owner_email?: string;
    /** meeting_booked (JT1-1920): only bookings on these JobTetris booking links (none = any),
     *  and who to enrol: candidates, contacts or either ('any', the default). */
    event_type_ids?: string[];
    person_type?: 'candidate' | 'contact' | 'any';
  };
}

export interface Campaign {
  id: string;
  org_id: string;
  name: string;
  description?: string | null;
  type: CampaignType;
  status: CampaignStatus;
  owner_user_id: string;
  audience_id?: string | null;
  trigger_config?: TriggerConfig | null;
  /** Outreach mailbox allowlist — see backend Campaign.allowed_mailbox_ids.
   *  Null/empty array means "no restriction" (default routing through any
   *  of the owner's eligible mailboxes). NEWSLETTER campaigns ignore this. */
  allowed_mailbox_ids?: string[] | null;
  /** SmsNumber id this campaign sends SMS from. Required for SMS_BLAST and
   *  any campaign with SMS steps; null otherwise. Provider-agnostic — the
   *  number row carries the provider discriminator. */
  sms_number_id?: string | null;
  /** Sprint 16b — per-campaign merge-token overlay. Flat dict, dotted
   *  keys like `newsletter.title`. Authored in the "Template inputs" form
   *  on the Settings step. Overlaid at send time; campaign values win. */
  merge_context?: Record<string, string> | null;
  /** Sprint 16c — soft ref to JobCache.jt_job_id. When set, the send-time
   *  merge dict auto-resolves `{{job.*}}` tokens from the cached row. */
  bound_job_id?: string | null;
  /** "Send from the deal/job owner's mailbox" override for trigger campaigns.
   *  null = inherit the org default; true/false = explicit override. */
  send_from_owner?: boolean | null;
  /** Why the last TASK step couldn't create its JT task (JT1-1854). Cleared
   *  by the next task that succeeds. */
  task_error?: string | null;
  task_error_at?: string | null;
  /** Why the campaign's texts are waiting or on hold (JT1-1858). Cleared by
   *  the next text that goes out. */
  sms_notice?: string | null;
  sms_notice_at?: string | null;
  launched_at?: string | null;
  paused_at?: string | null;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignDetail extends Campaign {
  sequence?: {
    id: string;
    steps: unknown[];
    max_steps: number;
    stop_on_reply: boolean;
    stop_on_bounce: boolean;
    stop_on_booking: boolean;
  } | null;
  audience?: { id: string; name: string; member_count: number; is_dynamic: boolean } | null;
  enrollment_count?: number;
}

export interface CampaignListResponse {
  data: Campaign[];
  meta: { total: number; page: number; limit: number };
}

export interface CampaignListFilters {
  status?: CampaignStatus | '';
  type?: CampaignType | '';
  owner?: string;
  page?: number;
  limit?: number;
  q?: string;
}

export function useCampaigns(filters?: CampaignListFilters) {
  const params: Record<string, string | number> = {};
  if (filters?.status) params.status = filters.status;
  if (filters?.type) params.type = filters.type;
  if (filters?.owner) params.owner = filters.owner;
  if (filters?.page) params.page = filters.page;
  if (filters?.limit) params.limit = filters.limit;
  if (filters?.q) params.q = filters.q;

  return useQuery<CampaignListResponse>({
    queryKey: ['campaigns', params],
    queryFn: async () => {
      const { data } = await apiClient.get<CampaignListResponse>('/campaigns', { params });
      return data;
    },
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery<CampaignDetail>({
    queryKey: ['campaigns', id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: CampaignDetail }>(`/campaigns/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export interface CampaignStepStat {
  step_number: number;
  type: 'EMAIL' | 'SMS' | 'DELAY' | 'TASK' | 'BRANCH' | 'EXIT';
  label: string | null;
  delay_days: number | null;
  in_queue: number;
  sent: number;
  completed: number;
  total_enrollments: number;
}

export function useCampaignStepStats(id: string | undefined) {
  return useQuery<CampaignStepStat[]>({
    queryKey: ['campaigns', id, 'step-stats'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: CampaignStepStat[] }>(`/campaigns/${id}/step-stats`);
      return data.data;
    },
    enabled: !!id,
    // Refresh every 30s so the in-queue counts feel live on active
    // campaigns without hammering the API.
    refetchInterval: 30_000,
  });
}

export interface CampaignWritable {
  name: string;
  type: CampaignType;
  description?: string | null;
  audience_id?: string | null;
  /** Pass `null` to clear an existing trigger; omit to leave unchanged. */
  trigger_config?: TriggerConfig | null;
  /** Pass `null` or an empty array to clear; omit to leave unchanged.
   *  Ignored for NEWSLETTER campaigns. */
  allowed_mailbox_ids?: string[] | null;
  /** Pass `null` to clear; omit to leave unchanged. */
  sms_number_id?: string | null;
  /** Sprint 16b — per-campaign merge-token overlay. Pass `null` to clear;
   *  omit to leave unchanged. Allowed at any campaign status (only
   *  affects future sends, not past). */
  merge_context?: Record<string, string> | null;
  /** Sprint 16c — bind to a JT job for {{job.*}} resolution. Pass `null`
   *  to unbind; omit to leave unchanged. Allowed at any campaign status. */
  bound_job_id?: string | null;
  /** Send-from-owner override: null = inherit org default, true/false = explicit.
   *  Omit to leave unchanged. Allowed at any campaign status (future sends only). */
  send_from_owner?: boolean | null;
  /** JT user id of the campaign owner. Allowed to change at any status. */
  owner_user_id?: string;
  /** When provided on create(), the new campaign's type, description,
   *  trigger config, and sequence are seeded from the named template.
   *  Caller-provided fields still win. */
  template_id?: string | null;
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CampaignWritable) => {
      const { data } = await apiClient.post<{ data: Campaign }>('/campaigns', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useUpdateCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CampaignWritable>) => {
      const { data } = await apiClient.patch<{ data: Campaign }>(`/campaigns/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

export function useLaunchCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: Campaign }>(`/campaigns/${id}/launch`);
      return data.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

export function usePauseCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: Campaign }>(`/campaigns/${id}/pause`);
      return data.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

export function useResumeCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: Campaign }>(`/campaigns/${id}/resume`);
      return data.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

export function useArchiveCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: Campaign }>(`/campaigns/${id}/archive`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/campaigns/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

/**
 * Post-launch mailbox-selection updater. Unlike useUpdateCampaign (DRAFT-only),
 * this hits a dedicated endpoint that allows changes after launch — future
 * sends pick up the new allowlist, past sends keep their sticky binding.
 */
export function useUpdateCampaignMailboxes(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { allowed_mailbox_ids: string[] | null }) => {
      const { data } = await apiClient.put<{ data: Campaign }>(`/campaigns/${id}/mailboxes`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

/**
 * Post-launch SMS-sender update. Mirrors useUpdateCampaignMailboxes — future
 * SMS sends pick up the new number; past sends keep their bound number via
 * the Message row. Provider-agnostic — the picker can list numbers from
 * any connected SMS provider.
 */
export function useUpdateCampaignSmsNumber(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { sms_number_id: string | null }) => {
      const { data } = await apiClient.put<{ data: Campaign }>(`/campaigns/${id}/sms-number`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaigns', id] });
    },
  });
}

/* ── Sequence (per campaign) ── */

export interface SequenceStep {
  id?: string;
  step_number: number;
  type: 'EMAIL' | 'SMS' | 'DELAY' | 'TASK' | 'BRANCH' | 'EXIT';
  delay_days?: number | null;
  /** Meeting-relative DELAY (Phase 2c). When set to 'meeting_start', the DELAY
   *  waits until the booked meeting's start + delay_offset_minutes (negative =
   *  before) instead of delay_days. Only meaningful for meeting_booked campaigns. */
  delay_anchor?: 'meeting_start' | null;
  delay_offset_minutes?: number | null;
  subject?: string | null;
  body_html?: string | null;
  body_text?: string | null;
  branch_condition?: unknown;
  template_id?: string | null;
  /** TASK step fields. `task_title` supports merge tokens like `{{first_name}}`
   *  resolved against candidate_cache at fire time. `task_assignee_id` falls
   *  back to the campaign owner when null. `task_due_days` is business days. */
  task_title?: string | null;
  task_assignee_id?: string | null;
  task_due_days?: number | null;
}

export interface SequencePayload {
  steps: SequenceStep[];
  max_steps?: number;
  stop_on_reply?: boolean;
  stop_on_bounce?: boolean;
  stop_on_booking?: boolean;
}

export interface Sequence extends SequencePayload {
  id: string;
  campaign_id: string;
}

export function useCampaignSequence(campaignId: string | undefined) {
  return useQuery<Sequence | null>({
    queryKey: ['campaigns', campaignId, 'sequence'],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get<{ data: Sequence }>(`/campaigns/${campaignId}/sequence`);
        return data.data;
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) return null;
        throw err;
      }
    },
    enabled: !!campaignId,
  });
}

export function useSaveCampaignSequence(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SequencePayload) => {
      const { data } = await apiClient.put<{ data: Sequence }>(`/campaigns/${campaignId}/sequence`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns', campaignId, 'sequence'] });
      qc.invalidateQueries({ queryKey: ['campaigns', campaignId] });
    },
  });
}
